import { Download, Upload } from "lucide-react";
import { Button } from "@/components/Base/Button";
import { Tooltip } from "@/components/Common/Tooltip";
import { useState } from "react";
import { LayoutNameDialog } from "@/components/Dialogs/LayoutNameDialog";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { Container, Layout } from "@/types/v2";

interface ImportExportLayoutProps {
  currentLayout?: Layout[]; // The current layout to export
  existingLayoutNames: string[];
  containers: Container[];
  onImport: (
    layoutData: Layout[],
    layoutName: string,
    containers: Container[]
  ) => void;
}

export const ImportExportLayout = ({
  currentLayout,
  existingLayoutNames,
  onImport,
  containers,
}: ImportExportLayoutProps) => {
  const [importedLayout, setImportedLayout] = useState<Layout[] | null>(null);
  const [importedContainers, setImportedContainers] = useState<
    Container[] | null
  >(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  // Handle exporting the current layout
  const handleExportLayout = () => {
    if (!currentLayout) return;
    try {
      // Create a JSON string from the current layout
      const associatedContainers = currentLayout
        .map((layout) => layout.i)
        .map((id) => containers.find((container) => container.id === id))
        .filter((container) => container !== undefined)
        .map(
          (container): Container => ({
            id: container.id,
            displayName: container.displayName ?? container.id,
            tags: [],
          })
        );

      const layoutExportData = {
        containers: associatedContainers,
        layout: currentLayout,
      };
      const layoutJson = JSON.stringify(layoutExportData, null, 2);

      // Create a download link
      const blob = new Blob([layoutJson], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = `layout-${new Date().getTime()}.json`;
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showSuccessToast("Layout exported successfully");
    } catch (error) {
      console.error("Failed to export layout:", error);
      showErrorToast("Failed to export layout");
    }
  };

  // Handle importing a layout
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const layoutData = JSON.parse(e.target?.result as string);
        if (
          layoutData.containers === undefined ||
          layoutData.layout === undefined
        ) {
          throw new Error("Invalid layout file format");
        }
        setImportedLayout(layoutData.layout as Layout[]);
        setImportedContainers(layoutData.containers as Container[]);
        setIsImportDialogOpen(true);
      } catch (error) {
        console.error("Failed to parse layout file:", error);
        showErrorToast("Invalid layout file format");
      }
    };
    reader.readAsText(file);

    // Reset the input value so the same file can be selected again
    event.target.value = "";
  };

  // Handle confirming the import with a name
  const handleImportConfirm = (layoutName: string) => {
    if (importedLayout && importedContainers) {
      onImport(importedLayout, layoutName, importedContainers);
      setImportedLayout(null);
      setImportedContainers(null);
      showSuccessToast(`Layout "${layoutName}" imported successfully`);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        {/* Export Button */}
        <Tooltip message="Export layout as JSON">
          <Button
            onClick={handleExportLayout}
            size="lgIcon"
            disabled={!currentLayout}
            className={"disabled:bg-zinc-700"}
          >
            <Download />
          </Button>
        </Tooltip>

        {/* Import Button */}
        <Tooltip message="Import layout from JSON">
          <Button
            size="lgIcon"
            onClick={() =>
              document.getElementById("import-layout-input")?.click()
            }
          >
            <Upload />
            <input
              id="import-layout-input"
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="hidden"
            />
          </Button>
        </Tooltip>
      </div>

      <LayoutNameDialog
        open={isImportDialogOpen}
        setOpen={() => setIsImportDialogOpen(false)}
        type="new"
        existingLayoutNames={existingLayoutNames}
        onConfirm={(name) => {
          handleImportConfirm(name);
          setIsImportDialogOpen(false);
        }}
      />
    </>
  );
};
